import { MonthPicker } from "@mui/x-date-pickers";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { SHButton, SHStack } from "@components/design-systems";
import { ArrowButton } from "@components/buttons/arrow";
import { useTheme } from "@mui/material";
import { debounce, defaultTo, indexOf, isNil } from "lodash";
import { SHNumericFieldV2 } from "@components/design-systems/sh-numeric-field-v2";
import { generateUUID } from "@utils";
import {
  clearStyles,
  getMonthRangeDate,
  setButtonDisabledStyles,
  setEndMonthRangeStyles,
  setStartMonthRangeStyles,
} from "@components/design-systems/sh-month-picker/utils";
import { getMonth, getYear } from "date-fns";
import { useMount } from "react-use";

export interface SHMonthRangePickerRef {
  reset: (startDate: Date | null, endDate: Date | null) => void;
}

type SHMonthRangePickerProps = {
  minYear: number;
  maxYear: number;
  pickEndOfMonth?: boolean;
  defaultStartDate?: Date | null;
  initialStartDate?: Date | null;
  defaultEndDate?: Date | null;
  initialEndDate?: Date | null;
  allowNullStartDate?: boolean;
  allowNullEndDate?: boolean;
  onChange: (range: [Date | null, Date | null]) => void;
};

export const SHMonthRangePicker = forwardRef<
  SHMonthRangePickerRef,
  SHMonthRangePickerProps
>(
  (
    {
      minYear,
      maxYear,
      pickEndOfMonth = false,
      defaultStartDate,
      initialStartDate,
      defaultEndDate,
      initialEndDate,
      allowNullStartDate = true,
      allowNullEndDate = true,
      onChange,
    }: SHMonthRangePickerProps,
    ref,
  ) => {
    const { palette } = useTheme();
    const [startMonthKey, setStartMonthKey] = useState<string>(generateUUID());
    const [endMonthKey, setEndMonthKey] = useState<string>(generateUUID());
    const [startYearKey, setStartYearKey] = useState<string>(generateUUID());
    const [endYearKey, setEndYearKey] = useState<string>(generateUUID());
    const startMonthRef = useRef<HTMLDivElement | null>(null);
    const endMonthRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      reset: (startDate, endDate) => {
        setStartMonth(startDate);
        setEndMonth(endDate);
        setStartYear(startDate ? getYear(startDate) : null);
        setEndYear(endDate ? getYear(endDate) : null);
        reRenderStartMonth();
        reRenderEndMonth();
      },
    }));

    const [startMonth, setStartMonth] = useState<Date | null>(
      defaultStartDate ? defaultStartDate : null,
    );
    const [endMonth, setEndMonth] = useState<Date | null>(
      defaultEndDate ? defaultEndDate : null,
    );
    const [startYear, setStartYear] = useState<number | null>(
      defaultStartDate ? defaultStartDate.getFullYear() : null,
    );
    const [endYear, setEndYear] = useState<number | null>(
      defaultEndDate ? defaultEndDate.getFullYear() : null,
    );

    const yearRange: number[] = useMemo(() => {
      const numberOfYears = maxYear - minYear + 1;
      return Array.from(
        { length: numberOfYears },
        (_, index) => minYear + index,
      );
    }, [minYear, maxYear]);

    const handleOnStartYearArrowButtonClick = (type: "prev" | "next") => {
      if (isNil(startYear)) {
        const currentYear = getYear(new Date());
        let newStartYear: number | null;
        if (type === "next") {
          if (isNil(endYear) || isNil(endMonth)) {
            newStartYear = currentYear;
          } else {
            newStartYear = endYear;
          }
        } else {
          if (isNil(endYear) || isNil(endMonth)) {
            newStartYear =
              currentYear - 1 > minYear ? currentYear - 1 : minYear;
          } else {
            newStartYear = endYear - 1 > minYear ? endYear - 1 : minYear;
          }
        }

        setStartYear(newStartYear);
        handleOnStartMonthChange({
          value: !isNil(startMonth) ? startMonth : new Date(),
          initStartYearValue: newStartYear,
        });

        return;
      }

      const currentStartYear = defaultTo(startYear, minYear);
      const idx = indexOf(yearRange, currentStartYear);

      if (type === "prev") {
        const newStartYear = idx > 0 ? yearRange[idx - 1] : currentStartYear;
        setStartYear(newStartYear);
        handleOnStartMonthChange({
          value: !isNil(startMonth) ? startMonth : endMonth,
          initStartYearValue: newStartYear,
        });

        return;
      }

      let newStartYear: number | null =
        idx < yearRange.length - 1 ? yearRange[idx + 1] : currentStartYear;
      newStartYear = isNil(newStartYear) ? minYear : newStartYear;
      if (newStartYear >= defaultTo(endYear, maxYear)) newStartYear = endYear;
      setStartYear(newStartYear);
      handleOnStartMonthChange({
        value: isNil(startMonth) ? endMonth : startMonth,
        initStartYearValue: newStartYear,
      });
    };

    const handleOnEndYearArrowButtonClick = (type: "prev" | "next") => {
      if (isNil(endYear)) {
        const currentYear = getYear(new Date());
        let newEndYear: number | null;
        if (type === "prev") {
          if (isNil(startYear) || isNil(startMonth)) {
            newEndYear = currentYear;
          } else {
            newEndYear = startYear;
          }
        } else {
          if (isNil(startYear) || isNil(startMonth)) {
            newEndYear = currentYear + 1 < maxYear ? currentYear + 1 : maxYear;
          } else {
            newEndYear = startYear + 1 < maxYear ? startYear + 1 : maxYear;
          }
        }

        setEndYear(newEndYear);
        handleOnEndMonthChange({
          value: !isNil(endMonth) ? endMonth : new Date(),
          initEndYearValue: newEndYear,
        });

        return;
      }

      const currentEndYear = defaultTo(endYear, maxYear);
      const idx = indexOf(yearRange, currentEndYear);

      if (type === "prev") {
        const newEndYear = idx > 0 ? yearRange[idx - 1] : currentEndYear;
        setEndYear(newEndYear);
        handleOnEndMonthChange({
          value: !isNil(endMonth) ? endMonth : startMonth,
          initEndYearValue: newEndYear,
        });
        return;
      }

      let newEndYear =
        idx < yearRange.length - 1 ? yearRange[idx + 1] : currentEndYear;
      newEndYear = isNil(newEndYear) ? maxYear : newEndYear;
      const currentStartYear = isNil(startYear) ? minYear : startYear;
      if (newEndYear <= currentStartYear) newEndYear = currentStartYear;
      setEndYear(newEndYear);
      handleOnEndMonthChange({
        value: isNil(endMonth) ? startMonth : endMonth,
        initEndYearValue: newEndYear,
      });
    };

    const handleOnStartYearInputBlur = (
      e:
        | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        | React.FocusEvent<HTMLInputElement>,
    ) => {
      const value = e.target.value;
      const floatValue = parseFloat(value);
      const isValid = !isNaN(floatValue);

      if (!isValid) {
        reRenderStartMonth();
        return;
      }

      let newStartYear: number | null = Number(value);
      let newStartMonth = !isNil(startMonth)
        ? startMonth
        : !isNil(endMonth)
        ? endMonth
        : new Date();
      if (newStartYear < minYear) newStartYear = minYear;
      if (newStartYear > defaultTo(endYear, maxYear))
        newStartYear = isNil(endYear) ? maxYear : endYear;

      if (!isNil(endYear) && !isNil(endMonth) && newStartYear >= endYear) {
        if (getMonth(endMonth) === 0) {
          newStartYear -= 1;
          newStartMonth.setMonth(11);
        } else if (
          newStartYear === endYear &&
          getMonth(endMonth) <= getMonth(newStartMonth)
        ) {
          if (getMonth(endMonth) === 0) {
            newStartYear -= 1;
            newStartMonth = new Date(newStartYear, 11, 1);
          } else {
            newStartMonth = new Date(newStartYear, getMonth(endMonth) - 1, 1);
          }
        }
      }

      handleOnStartMonthChange({
        value: newStartMonth,
        initStartYearValue: newStartYear,
      });
      setStartYearKey(generateUUID());
    };

    const handleOnEndYearInputBlur = (
      e:
        | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        | React.FocusEvent<HTMLInputElement>,
    ) => {
      const value = e.target.value;
      const floatValue = parseFloat(value);
      const isValid = !isNaN(floatValue);

      if (!isValid) {
        reRenderEndMonth();
        return;
      }

      let newEndYear: number | null = Number(value);
      let newEndMonth = !isNil(endMonth)
        ? endMonth
        : !isNil(startMonth)
        ? startMonth
        : new Date();

      if (newEndYear > maxYear) newEndYear = maxYear;
      if (newEndYear < defaultTo(startYear, minYear))
        newEndYear = !isNil(startYear) ? startYear : minYear;
      if (!isNil(startYear) && !isNil(startMonth) && newEndYear <= startYear) {
        if (getMonth(startMonth) === 11) {
          newEndYear += 1;
          newEndMonth.setMonth(0);
        } else if (
          newEndYear === startYear &&
          getMonth(startMonth) >= getMonth(newEndMonth)
        ) {
          if (getMonth(startMonth) === 11) {
            newEndYear += 1;
            newEndMonth = new Date(newEndYear, 0, 1);
          } else {
            newEndMonth = new Date(newEndYear, getMonth(startMonth) + 1, 1);
          }
        }
      }

      handleOnEndMonthChange({
        value: newEndMonth,
        initEndYearValue: newEndYear,
      });
      setEndYearKey(generateUUID());
    };

    const handleOnStartMonthChange = ({
      value,
      initStartYearValue,
      initEndYearValue,
      initEndMonthValue,
    }: {
      value: Date | null;
      initStartYearValue?: number | null;
      initEndYearValue?: number | null;
      initEndMonthValue?: number | null;
    }) => {
      if (!value) return;

      // Extract month and year values with proper defaults
      const endMonthValue = isNil(initEndMonthValue)
        ? endMonth
          ? getMonth(endMonth)
          : null
        : initEndMonthValue;
      const endYearValue = isNil(initEndYearValue)
        ? isNil(endYear)
          ? null
          : endYear
        : initEndYearValue;

      // Determine start year value
      let startYearValue: number;
      if (!isNil(initStartYearValue)) {
        startYearValue = initStartYearValue;
      } else if (!isNil(startYear)) {
        startYearValue = startYear;
      } else {
        // Default to current year if no start year is set
        startYearValue = getYear(new Date());
        // Ensure start year doesn't exceed end year if end year exists
        if (!isNil(endYearValue) && startYearValue > endYearValue) {
          startYearValue = endYearValue;
        }
        // If end month is January, adjust start year to previous year
        if (
          endMonthValue !== null &&
          endMonthValue === 0 &&
          startYearValue === endYearValue
        ) {
          startYearValue -= 1;
        }
      }

      // Get month from provided value or default to current month
      const startMonthValue = value ? getMonth(value) : getMonth(new Date());

      // Handle case when start and end dates are not in the same year or end date is not set
      if (
        startYearValue !== endYearValue ||
        isNil(endMonthValue) ||
        isNil(endYearValue)
      ) {
        setStartYear(startYearValue);

        // Prevent selecting December in max year (adjust to November)
        const valueToSet =
          startYearValue === maxYear && getMonth(value) === 11
            ? new Date(startYearValue, 10, 1)
            : value;

        setStartMonth(valueToSet);
        return;
      }

      // Handle special cases when start and end dates are in the same year
      let startDate: Date | null;
      if (endMonthValue === 0) {
        // If end month is January, set start to December of previous year
        startDate = new Date(endYearValue - 1, 11, 1);
      } else if (startMonthValue >= endMonthValue && endMonthValue !== 0) {
        // If selected start month is after end month, set to month before end
        startDate = new Date(startYearValue, endMonthValue - 1, 1);
      } else {
        // Use selected month
        startDate = new Date(startYearValue, startMonthValue, 1);
      }

      setStartMonth(startDate);
      setStartYear(startDate ? getYear(startDate) : startYear);
    };

    const handleOnEndMonthChange = ({
      value,
      initEndYearValue,
      initStartMonthValue,
      initStartYearValue,
    }: {
      value: Date | null;
      initEndYearValue?: number | null;
      initStartMonthValue?: number | null;
      initStartYearValue?: number | null;
    }) => {
      if (!value) return;

      // Extract month and year values with proper defaults
      const startMonthValue = isNil(initStartMonthValue)
        ? startMonth
          ? getMonth(startMonth)
          : null
        : initStartMonthValue;
      const startYearValue = isNil(initStartYearValue)
        ? isNil(startYear)
          ? null
          : startYear
        : initStartYearValue;

      // Determine end year value
      let endYearValue: number;
      if (!isNil(initEndYearValue)) {
        endYearValue = initEndYearValue;
      } else if (!isNil(endYear)) {
        endYearValue = endYear;
      } else {
        // Default to current year if no end year is set
        endYearValue = getYear(new Date());
        // Ensure end year doesn't fall below start year if start year exists
        if (!isNil(startYearValue) && endYearValue < startYearValue) {
          endYearValue = startYearValue;
        }
        // If start month is December, adjust end year to next year
        if (
          startMonthValue !== null &&
          startMonthValue === 11 &&
          endYearValue === startYearValue
        ) {
          endYearValue += 1;
        }
      }

      // Get month from provided value or default to current month
      const endMonthValue = value ? getMonth(value) : getMonth(new Date());

      // Handle case when start and end dates are not in the same year or start date is not set
      if (
        startYearValue !== endYearValue ||
        isNil(startMonthValue) ||
        isNil(startYearValue)
      ) {
        setEndYear(endYearValue);

        // Prevent selecting January in min year (adjust to February)
        const valueToSet =
          endYearValue === minYear && getMonth(value) === 0
            ? new Date(endYearValue, 1, 1)
            : value;

        setEndMonth(valueToSet);
        return;
      }

      // Handle special cases when start and end dates are in the same year
      let endDate: Date | null;
      if (startMonthValue === 11) {
        // If start month is December, set end to January of next year
        endDate = new Date(startYearValue + 1, 0, 1);
      } else if (endMonthValue <= startMonthValue && startMonthValue !== 11) {
        // If selected end month is before or equal to start month, set to month after start
        endDate = new Date(endYearValue, startMonthValue + 1, 1);
      } else {
        // Use selected month
        endDate = new Date(endYearValue, endMonthValue, 1);
      }
      setEndMonth(endDate);
      setEndYear(endDate ? getYear(endDate) : endYear);
    };

    const handleOnClearStartMonth = () => {
      if (!allowNullStartDate) return;
      setStartMonth(null);
      setStartYear(null);
      reRenderStartMonth();
    };

    const handleOnClearEndMonth = () => {
      if (!allowNullEndDate) return;
      setEndMonth(null);
      setEndYear(null);
      reRenderEndMonth();
    };

    const reRenderStartMonth = () => setStartMonthKey(generateUUID());
    const reRenderEndMonth = () => setEndMonthKey(generateUUID());

    const setStartMonthStyles = () => {
      setStartMonthRangeStyles({
        element: startMonthRef.current,
        month: startMonth !== null ? getMonth(startMonth) : null,
        palette,
      });
    };

    const setEndMonthStyles = () => {
      setEndMonthRangeStyles({
        element: endMonthRef.current,
        month: endMonth !== null ? getMonth(endMonth) : null,
        palette,
      });
    };

    const clearStartMonthStyles = () => {
      clearStyles({
        element: startMonthRef.current,
        palette,
      });
    };

    const clearEndMonthStyles = () => {
      clearStyles({
        element: endMonthRef.current,
        palette,
      });
    };

    const handleOnDateRangeValueChange = ({
      startYearValue,
      endYearValue,
      startMonthValue,
      endMonthValue,
    }: {
      startYearValue?: number | null;
      endYearValue?: number | null;
      startMonthValue?: Date | null;
      endMonthValue?: Date | null;
    }) => {
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      const targetStartYear = isNil(startYearValue)
        ? startYear
        : startYearValue;
      const targetEndYear = isNil(endYearValue) ? endYear : endYearValue;
      const targetStartMonth = isNil(startMonthValue)
        ? startMonth
        : startMonthValue;
      const targetEndMonth = isNil(endMonthValue) ? endMonth : endMonthValue;

      // Handle start date calculation
      if (!isNil(targetStartYear) && !isNil(targetStartMonth)) {
        startDate = getMonthRangeDate({
          year: targetStartYear,
          month: getMonth(targetStartMonth),
          pickEndOfMonth,
        });
      } else if (!allowNullStartDate) {
        startDate = getMonthRangeDate({
          year: minYear,
          month: 0,
          pickEndOfMonth,
        });
      }

      // Handle end date calculation
      if (!isNil(targetEndYear) && !isNil(targetEndMonth)) {
        endDate = getMonthRangeDate({
          year: targetEndYear,
          month: getMonth(targetEndMonth),
          pickEndOfMonth,
        });
      } else if (!allowNullEndDate) {
        endDate = getMonthRangeDate({
          year: maxYear,
          month: 0,
          pickEndOfMonth,
        });
      }

      onChange([startDate, endDate]);
    };

    useEffect(() => {
      handleOnDateRangeValueChange({});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startYear, endYear, startMonth, endMonth]);

    const disableNextStartYear = useMemo(() => {
      // Early returns for cases where button should NOT be disabled
      if (!startYear || !startMonth) return false;

      // Cases where button SHOULD be disabled
      if (startYear >= maxYear) return true;
      if (endYear && startYear >= endYear) return true;

      // Special case: December of current year to January of next year
      if (
        endYear &&
        endMonth &&
        startYear === endYear - 1 &&
        getMonth(endMonth) === 0
      ) {
        return true;
      }

      // Default case: not disabled
      return false;
    }, [endMonth, endYear, maxYear, startMonth, startYear]);

    const disablePrevEndYear = useMemo(() => {
      // Early returns for cases where button should NOT be disabled
      if (!endYear || !endMonth) return false;

      // Cases where button SHOULD be disabled
      if (endYear <= minYear) return true;
      if (startYear && endYear <= startYear) return true;

      // Special case: January of next year to December of current year
      if (
        startYear &&
        startMonth &&
        endYear === startYear + 1 &&
        getMonth(startMonth) === 11
      ) {
        return true;
      }

      // Default case: not disabled
      return false;
    }, [endMonth, endYear, minYear, startMonth, startYear]);

    useMount(() => {
      if (!!initialStartDate && !defaultStartDate) {
        setStartYear(getYear(initialStartDate));
        setStartMonth(initialStartDate);
      }

      if (!!initialEndDate && !defaultEndDate) {
        setEndYear(getYear(initialEndDate));
        setEndMonth(initialEndDate);
      }
    });

    const debouncePaint = debounce(() => {
      !isNil(startMonth) ? setStartMonthStyles() : clearStartMonthStyles();
      !isNil(endMonth) ? setEndMonthStyles() : clearEndMonthStyles();

      if (startYear === endYear) {
        setButtonDisabledStyles({
          palette,
          startElement: startMonthRef.current,
          endElement: endMonthRef.current,
          endMonth,
          startMonth,
        });
      }
    }, 100);

    useEffect(() => {
      debouncePaint();

      return () => debouncePaint.cancel();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      startYear,
      startYearKey,
      startMonthKey,
      startMonth,
      startMonthRef.current,
      endYear,
      endYearKey,
      endMonthKey,
      endMonth,
      endMonthRef.current,
    ]);

    return (
      <SHStack direction={"row"} gap={2}>
        <SHStack key={startMonthKey} gap={1.5} alignItems="center">
          <SHStack
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"220px"}
          >
            <ArrowButton
              disabled={!!startYear && startYear <= minYear}
              direction={"previous"}
              hidden={false}
              onClick={() => handleOnStartYearArrowButtonClick("prev")}
            />
            <SHStack
              width={140}
              height={32}
              borderRadius={"24px"}
              bgcolor={"#F2F1FF"}
              justifyItems={"center"}
              alignContent={"center"}
              alignItems={"center"}
            >
              <SHStack width={35}>
                <SHNumericFieldV2
                  key={startYearKey}
                  value={startYear ? String(startYear) : startYear}
                  valueIsNumericString
                  sx={{
                    height: "32px",
                    ".MuiInput-root": {
                      "&:before": {
                        bottom: "5px",
                      },
                      "&:after": {
                        bottom: "5px",
                      },
                    },
                    ".MuiInput-root input": {
                      width: "35px !important",
                      padding: 0,
                      fontWeight: 700,
                    },
                  }}
                  onBlur={handleOnStartYearInputBlur}
                />
              </SHStack>
            </SHStack>
            <ArrowButton
              direction={"next"}
              disabled={disableNextStartYear}
              hidden={false}
              onClick={() => handleOnStartYearArrowButtonClick("next")}
            />
          </SHStack>
          <SHStack>
            <MonthPicker
              ref={startMonthRef}
              date={startMonth}
              disableHighlightToday={startMonth === null}
              onChange={(value) => handleOnStartMonthChange({ value })}
              sx={{
                justifyContent: "center",
                width: "220px",
                height: "180px",
                gridColumnGap: "5px",
                button: {
                  borderRadius: "8px",
                  maxWidth: "70px",
                  height: "40px",
                  marginY: "2px",
                  fontSize: "14px",
                },
              }}
            />
            {allowNullStartDate && (
              <SHStack alignItems={"end"}>
                <SHButton
                  size={"small"}
                  color={"primary"}
                  variant={"text"}
                  disabled={!startYear && !startMonth}
                  onClick={handleOnClearStartMonth}
                >
                  Clear
                </SHButton>
              </SHStack>
            )}
          </SHStack>
        </SHStack>

        <SHStack key={endMonthKey} gap={1.5} alignItems="center">
          <SHStack
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"220px"}
          >
            <ArrowButton
              disabled={disablePrevEndYear}
              direction={"previous"}
              hidden={false}
              onClick={() => handleOnEndYearArrowButtonClick("prev")}
            />
            <SHStack
              width={140}
              height={32}
              borderRadius={"24px"}
              bgcolor={"#F2F1FF"}
              justifyItems={"center"}
              alignItems={"center"}
              alignContent={"center"}
            >
              <SHStack width={35}>
                <SHNumericFieldV2
                  key={endYearKey}
                  value={endYear ? String(endYear) : endYear}
                  valueIsNumericString
                  sx={{
                    height: "32px",
                    ".MuiInput-root": {
                      "&:before": {
                        bottom: "5px",
                      },
                      "&:after": {
                        bottom: "5px",
                      },
                    },
                    ".MuiInput-root input": {
                      width: "35px !important",
                      padding: 0,
                      fontWeight: 700,
                    },
                  }}
                  onBlur={handleOnEndYearInputBlur}
                />
              </SHStack>
            </SHStack>
            <ArrowButton
              direction={"next"}
              disabled={
                defaultTo(endYear, getYear(new Date())) >= maxYear &&
                !isNil(endYear)
              }
              hidden={false}
              onClick={() => handleOnEndYearArrowButtonClick("next")}
            />
          </SHStack>
          <SHStack>
            <MonthPicker
              ref={endMonthRef}
              date={endMonth}
              disableHighlightToday={endMonth === null}
              onChange={(value) => handleOnEndMonthChange({ value })}
              sx={{
                justifyContent: "center",
                width: "220px",
                height: "180px",
                gridColumnGap: "5px",
                button: {
                  borderRadius: "8px",
                  maxWidth: "70px",
                  height: "40px",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  marginY: "2px",
                  fontSize: "14px",
                },
              }}
            />
            {allowNullEndDate && (
              <SHStack alignItems={"end"}>
                <SHButton
                  size={"small"}
                  color={"primary"}
                  variant={"text"}
                  disabled={!endYear && !endMonth}
                  onClick={handleOnClearEndMonth}
                >
                  Clear
                </SHButton>
              </SHStack>
            )}
          </SHStack>
        </SHStack>
      </SHStack>
    );
  },
);
